import React, { useRef, useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { motion, AnimatePresence } from "framer-motion";
import { FaUser, FaSignOutAlt, FaCog, FaUserShield, FaThLarge } from "react-icons/fa";
import { GoArrowSwitch } from "react-icons/go";
import {
  IconDashboard,
  IconInbox,
  IconEmailAccount,
  IconLeads,
  IconCampaigns,
} from "@assets/sidebarIcons";
import { RootState } from "@/store";
import { logout } from "@/store/slices/authSlice";
import api from "@/api/config";
import Tooltip from "@/components/Tooltip";
import NotificationCenter from "@/components/NotificationCenter";
import { useAccountSettingsModal } from "@/contexts/AccountSettingsModalContext";

const navItems = [
  { path: "/dashboard", icon: IconDashboard, label: "Dashboard" },
  { path: "/master_inbox", icon: IconInbox, label: "Master Inbox" },
  { path: "/email_accounts", icon: IconEmailAccount, label: "Email Accounts" },
  { path: "/lead_discovery", icon: IconLeads, label: "Lead Discovery" },
  { path: "/my-campaigns", icon: IconCampaigns, label: "Campaigns" },
];

const badgeColors: Record<string, string> = {
  danger: "bg-red-500",
  warning: "bg-amber-500",
  success: "bg-emerald-500",
  "success pulse": "bg-emerald-500 animate-pulse",
};

const SidebarNav: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { openModal } = useAccountSettingsModal();

  const { data: accountsData } = useQuery(
    "emailAccountsStatus",
    async () => {
      const res = await api.get("/email_accounts", {
        headers: { Accept: "application/json" },
      });
      return res.data;
    },
    { refetchInterval: 60000, retry: false },
  );

  const { data: campaignStates } = useQuery(
    "campaignStates",
    async () => {
      const res = await api.get("/api/campaign-states");
      return res.data;
    },
    { refetchInterval: 30000, retry: false },
  );

  const { data: unreadRepliesData } = useQuery(
    "unreadReplies",
    async () => {
      const res = await api.get("/api/unread-replies");
      return res.data;
    },
    { refetchInterval: 60000, retry: false },
  );

  const accountsNeedingReconnection =
    accountsData?.accounts?.filter(
      (a: any) => a.needs_reauth || a.status === "needs_reauth",
    ).length || 0;
  const processingCampaigns = campaignStates?.processing || 0;
  const completedUnsentCampaigns = campaignStates?.completed_unsent || 0;
  const unreadReplies = unreadRepliesData?.unread_replies || 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    dispatch(logout());
    window.location.href = "/login";
  };

  const getBadge = (path: string) => {
    if (path === "/email_accounts" && accountsNeedingReconnection > 0)
      return { count: accountsNeedingReconnection, type: "danger" };
    if (path === "/my-campaigns" && processingCampaigns > 0)
      return { count: processingCampaigns, type: "warning" };
    if (path === "/my-campaigns" && completedUnsentCampaigns > 0)
      return { count: completedUnsentCampaigns, type: "success" };
    if (path === "/master_inbox" && unreadReplies > 0)
      return { count: unreadReplies, type: "success pulse" };
    return null;
  };

  const avatarLetter = user?.name?.charAt(0).toUpperCase() ?? "?";
  const isAdmin = user?.is_admin === true;

  return (
    <aside className="fixed top-0 left-0 h-screen flex flex-col items-center py-3 pt-6 z-[99] w-auto p-2">
      <div className="flex flex-col justify-between h-full">
        <Link
          to="/dashboard"
          className="flex items-center justify-center mb-5 flex-shrink-0 no-underline"
          aria-label="Dashboard"
        >
          <span className="text-[#006EFF] text-[22px] leading-none">
            <FaThLarge />
          </span>
        </Link>

        <nav className="flex flex-col items-center gap-2 mt-3 w-full flex-1">
          {navItems.map((item, i) => {
            const badge = getBadge(item.path);
            return (
              <div
                key={item.path}
                className="relative w-full flex flex-col items-center"
              >
                <Tooltip content={item.label} position="right">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `relative w-[46px] h-[38px] rounded-lg flex items-center justify-center no-underline transition-all duration-150 border-none cursor-pointer
                    ${
                      isActive
                        ? "!bg-[#E1EEFF] text-[#006EFF]"
                        : "bg-transparent text-[#17150B] hover:bg-[#F1F5F9]"
                    }`
                    }
                  >
                    <item.icon className="w-[22px] h-[22px] flex-shrink-0" />
                    {badge && (
                      <span
                        className={`absolute top-0.5 right-0.5 min-w-[14px] h-[14px] rounded-full text-[9px] font-bold flex items-center justify-center px-0.75 text-white pointer-events-none ${badgeColors[badge.type]}`}
                      >
                        {badge.count}
                      </span>
                    )}
                  </NavLink>
                </Tooltip>

                {i === 1 && (
                  <div className="w-5 h-px border border-[#E3E2E7] my-1.5" />
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex flex-col items-center gap-1.5 w-full px-0.75">
          {/* Notification bell with popup */}
          <div className="sidebar-notification-wrapper">
            <NotificationCenter />
          </div>

          {/* Admin panel button (only for admins) */}
          {isAdmin && (
            <Tooltip content="Admin Panel" position="right">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="w-[46px] h-[38px] rounded-lg flex items-center justify-center transition-all duration-150 border-none cursor-pointer bg-transparent text-[#17150B] hover:bg-[#F1F5F9]"
                aria-label="Admin Panel"
              >
                <FaUserShield className="w-[22px] h-[22px]" />
              </button>
            </Tooltip>
          )}

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              className="w-[30px] h-[30px] rounded-full border-none cursor-pointer overflow-hidden flex items-center justify-center bg-gradient-to-br from-amber-400 to-emerald-400 text-white font-bold text-[12px] p-0"
              onClick={() => setIsUserMenuOpen((v) => !v)}
              title={user?.name}
            >
              {user?.profile_pic ? (
                <img
                  src={user.profile_pic}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{avatarLetter}</span>
              )}
            </button>

            <AnimatePresence>
              {isUserMenuOpen && (
                <motion.div
                  className="absolute bottom-0 left-[calc(100%+10px)] bg-white border border-[#E3E2E7] rounded-xl shadow-xl min-w-[160px] overflow-hidden z-[1100]"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.15 }}
                >
                  <button
                    className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#17150B] hover:bg-[#F1F5F9] transition-colors w-full text-left bg-transparent border-none cursor-pointer"
                    onClick={() => {
                      openModal();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <FaUser className="w-3.5 h-3.5 text-[#7D7B78]" /> Profile
                  </button>
                  {isAdmin && (
                    <Link
                      to="/admin/dashboard"
                      className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#17150B] no-underline hover:bg-[#F1F5F9] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <GoArrowSwitch className="w-3.5 h-3.5 text-[#7D7B78]" />{" "}
                      Switch to Admin
                    </Link>
                  )}
                  {!isAdmin && (
                    <Link
                      to="/subscription"
                      className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#17150B] no-underline hover:bg-[#F1F5F9] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <FaCog className="w-3.5 h-3.5 text-[#7D7B78]" />{" "}
                      Subscription
                    </Link>
                  )}
                  <button
                    className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#17150B] hover:bg-[#F1F5F9] transition-colors w-full text-left bg-transparent border-none cursor-pointer"
                    onClick={handleLogout}
                  >
                    <FaSignOutAlt className="w-3.5 h-3.5 text-[#7D7B78]" />{" "}
                    Logout
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default SidebarNav;
